// src/components/Branch/config/sections/SEOSection.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SearchCircleIcon,
  PencilIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { updateSEOThunk } from "../../../../store/thunks/branchDisplayThunks";

const SEOSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { seo, loadingSection } = useSelector((state) => state.branchDisplay);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    metaTitle: "",
    metaDescription: "",
    keywords: [],
    canonicalUrl: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    structuredData: {
      enabled: false,
      businessType: "JewelryStore",
    },
  });
  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    if (seo) {
      setFormData({
        metaTitle: seo.metaTitle || "",
        metaDescription: seo.metaDescription || "",
        keywords: seo.keywords || [],
        canonicalUrl: seo.canonicalUrl || "",
        ogTitle: seo.ogTitle || "",
        ogDescription: seo.ogDescription || "",
        ogImage: seo.ogImage || "",
        structuredData: {
          enabled: seo.structuredData?.enabled ?? false,
          businessType: seo.structuredData?.businessType || "JewelryStore",
        },
      });
    }
  }, [seo]);

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }));
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (index) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    await dispatch(updateSEOThunk({ branchCode, seoData: formData }));
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <SearchCircleIcon className="h-4 w-4 mr-2" />
            SEO Configuration
          </h4>
          {isRootAdmin && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            {/* Meta Title */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Meta Title
                <span className="text-gray-400 ml-2">
                  ({formData.metaTitle.length}/60)
                </span>
              </label>
              <input
                type="text"
                value={formData.metaTitle}
                onChange={(e) => handleInputChange("metaTitle", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Page title for search engines"
                maxLength={60}
              />
            </div>

            {/* Meta Description */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Meta Description
                <span className="text-gray-400 ml-2">
                  ({formData.metaDescription.length}/160)
                </span>
              </label>
              <textarea
                value={formData.metaDescription}
                onChange={(e) => handleInputChange("metaDescription", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows="2"
                placeholder="Brief description for search results"
                maxLength={160}
              />
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Keywords</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddKeyword())
                  }
                  className="flex-1 px-3 py-2 border rounded-lg"
                  placeholder="Add keyword"
                />
                <button
                  type="button"
                  onClick={handleAddKeyword}
                  className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(index)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Canonical URL */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Canonical URL</label>
              <input
                type="url"
                value={formData.canonicalUrl}
                onChange={(e) => handleInputChange("canonicalUrl", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="https://..."
              />
            </div>

            {/* Open Graph */}
            <div className="border-t pt-4 mt-4">
              <h5 className="font-medium mb-3">Open Graph (Social Sharing)</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">OG Title</label>
                  <input
                    type="text"
                    value={formData.ogTitle}
                    onChange={(e) => handleInputChange("ogTitle", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Title for social shares"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">OG Image URL</label>
                  <input
                    type="url"
                    value={formData.ogImage}
                    onChange={(e) => handleInputChange("ogImage", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm text-gray-600 mb-1">OG Description</label>
                <textarea
                  value={formData.ogDescription}
                  onChange={(e) => handleInputChange("ogDescription", e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  placeholder="Description for social shares"
                />
              </div>
            </div>

            {/* Structured Data */}
            <div className="border-t pt-4 mt-4">
              <h5 className="font-medium mb-3">Structured Data (Schema.org)</h5>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.structuredData.enabled}
                    onChange={(e) =>
                      handleInputChange("structuredData.enabled", e.target.checked)
                    }
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm">Enable Structured Data</span>
                </label>
                {formData.structuredData.enabled && (
                  <select
                    value={formData.structuredData.businessType}
                    onChange={(e) =>
                      handleInputChange("structuredData.businessType", e.target.value)
                    }
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="JewelryStore">Jewelry Store</option>
                    <option value="Store">Store</option>
                    <option value="LocalBusiness">Local Business</option>
                  </select>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                onClick={handleSave}
                disabled={loadingSection === "seo"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                {loadingSection === "seo" ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <CheckCircleIcon className="h-4 w-4" />
                )}
                Save SEO Settings
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Meta Title:</span>
                <p className="font-medium">{seo?.metaTitle || "Not set"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Keywords:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {seo?.keywords?.length > 0 ? (
                    seo.keywords.map((kw, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-sm"
                      >
                        {kw}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>
            </div>
            {seo?.metaDescription && (
              <div>
                <span className="text-sm text-gray-600">Meta Description:</span>
                <p className="text-sm mt-1">{seo.metaDescription}</p>
              </div>
            )}
            <div className="flex items-center gap-4 pt-2">
              <span
                className={`px-2 py-1 rounded text-xs ${
                  seo?.structuredData?.enabled
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                Structured Data: {seo?.structuredData?.enabled ? "Enabled" : "Disabled"}
              </span>
              {seo?.canonicalUrl && (
                <a
                  href={seo.canonicalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  View Canonical URL
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOSection;
